import React, {
  Children,
  VFC,
  useEffect,
  useState,
  isValidElement,
  FC,
} from 'react';

import {
  Carousel,
  CarouselCard,
  CarouselSlider,
  CarouselViewport,
} from '@fluentui/react-carousel';
import classNames from 'classnames';

import styles from './Carousel.scss';
import useTranslate from '../../utils/translations';

export const Arrow: VFC<{
  right?: boolean;
  onClick: () => void;
}> = ({ right = false, onClick }) => {
  const t = useTranslate();

  return (
    <button
      onClick={onClick}
      className={classNames(styles.control, { [styles.right]: right })}
      aria-label={right ? t('next slide') : t('previous slide')}
    >
      <div className={styles.arrow}>
        <svg viewBox="0 0 100 100">
          <polyline
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
            points="65,85 25,50 65,15"
          ></polyline>
        </svg>
      </div>
    </button>
  );
};

export const Navigation: VFC<{
  count: number;
  selectedIndex: number;
  onClick: (index: number) => void;
}> = ({ count, selectedIndex, onClick }) => {
  const t = useTranslate();

  return (
    <div className={styles.navigation}>
      {Array.from({ length: count }).map((_, index) => (
        <button
          key={index}
          className={classNames(styles.indicator, {
            [styles.active]: index === selectedIndex,
          })}
          onClick={(): void => onClick(index)}
          aria-label={t('go to slide {{index}} of {{slideCount}}', {
            index: index + 1,
            slideCount: count,
          })}
          {...(index === selectedIndex ? { 'aria-current': 'page' } : {})}
        />
      ))}
    </div>
  );
};

const CarouselWrapper: FC<{ activeSlide: number }> = ({
  children,
  activeSlide,
}) => {
  const [selectedItem, setSelectedItem] = useState(
    activeSlide === -1 ? 0 : activeSlide,
  );

  useEffect(() => {
    const regex = /#(workspace_SpacesStore_.+?)$/;
    const match = regex.exec(window.location.href);
    if (match) {
      const blockId = match[1];

      const matchingIndex = Children.toArray(children).findIndex(
        (child) =>
          isValidElement(child) && child.props?.blockProps?.id === blockId,
      );

      if (matchingIndex !== -1) {
        setSelectedItem(matchingIndex);
      }
    }
  }, [children]);

  return (
    <Carousel activeIndex={selectedItem}>
      {selectedItem !== 0 && (
        <Arrow onClick={(): void => setSelectedItem(selectedItem - 1)} />
      )}
      <CarouselViewport draggable={false}>
        <CarouselSlider>
          {Children.map(children, (child, index) => (
            <CarouselCard key={index}>
              <div className={styles.slideContent}>{child}</div>
            </CarouselCard>
          ))}
        </CarouselSlider>
      </CarouselViewport>
      {selectedItem !== Children.count(children) - 1 && (
        <Arrow onClick={(): void => setSelectedItem(selectedItem + 1)} right />
      )}
      <Navigation
        count={Children.count(children)}
        selectedIndex={selectedItem}
        onClick={(index): void => setSelectedItem(index)}
      />
    </Carousel>
  );
};

export default CarouselWrapper;
